import React, { useCallback, useMemo } from 'react'

import { Modal, T, VStack } from 'govwell-ui'
import { SelectOption } from 'govwell-ui/components/Select/types'
import { ScrapeCrawlerType, ScrapeTemplateFragment } from 'types/graphql'

import { ConditionalFormFields } from 'src/components/form/ConditionalFormFields'
import { useNotification } from 'src/components/shared/Notification/NotificationContext'
import { useStartScrapeNowMutation } from 'src/fetch/scrapes'
import {
  useCreateScrapeTemplateMutation,
  useUpdateScrapeTemplateMutation,
} from 'src/fetch/scrapeTemplates'
import { useAppForm } from 'src/hooks/use-app-form'
import { useMyOrgId, useIsGovWellStaff } from 'src/layouts/AppStateContextLayout/utils'
import { RefetchFnType } from 'src/types'
import { useAnnounceFirstInvalidField } from 'src/utils/forms'

const CRAWLER_TYPE_OPTIONS: SelectOption<ScrapeCrawlerType>[] = [
  {
    value: 'FastHTMLOnly',
    label: 'Fast (HTML only)',
    sublabel: 'Recommended. Faster and more reliable, but only reads static HTML.',
  },
  {
    value: 'FullBrowser',
    label: 'Full browser (renders JavaScript)',
    sublabel:
      'Use for JavaScript-heavy sites. Catches more content but is slower and may have errors.',
  },
]

const getCrawlerTypeOptionKey = (option: SelectOption<ScrapeCrawlerType>) => option.value

const CRAWLER_TYPE_FIELDS = ['crawlerType']

type FormValues = {
  startUrl: string
  crawlerType: SelectOption<ScrapeCrawlerType> | null | undefined
  excludeUrlGlobs: string[]
}

type Props = {
  onClose: () => void
  existingScrapeTemplate?: ScrapeTemplateFragment
  refetch: RefetchFnType
}

const ScrapeTemplateModal = ({ onClose, existingScrapeTemplate, refetch }: Props) => {
  const { mutateAsync: createScrapeTemplate } = useCreateScrapeTemplateMutation()
  const { mutateAsync: updateScrapeTemplate } = useUpdateScrapeTemplateMutation()
  const { mutateAsync: startScrapeNow } = useStartScrapeNowMutation()
  const { notification } = useNotification()

  const announceFirstInvalidField = useAnnounceFirstInvalidField()
  const organizationId = useMyOrgId()
  const isGovWellStaff = useIsGovWellStaff()

  const isEditMode = !!existingScrapeTemplate

  const initialValues: FormValues = useMemo(() => {
    const crawlerTypeOption =
      CRAWLER_TYPE_OPTIONS.find((o) => o.value === existingScrapeTemplate?.crawlerType) ??
      CRAWLER_TYPE_OPTIONS[0]
    return {
      startUrl: existingScrapeTemplate?.startUrl ?? '',
      crawlerType: crawlerTypeOption,
      excludeUrlGlobs: existingScrapeTemplate?.excludeUrlGlobs ?? [],
    }
  }, [existingScrapeTemplate])

  const form = useAppForm({
    defaultValues: initialValues,
  })

  const handleSubmit = useCallback(async () => {
    await form.handleSubmit()
    if (!form.state.isValid) {
      announceFirstInvalidField()
      return Promise.reject()
    }

    const { crawlerType: crawlerTypeOption, ...rest } = form.state.values
    const input = { ...rest, crawlerType: crawlerTypeOption?.value }

    if (existingScrapeTemplate) {
      await updateScrapeTemplate({ id: existingScrapeTemplate.id, input })
    } else {
      const createScrapeTemplateResult = await createScrapeTemplate({
        input: { ...input, organizationId },
      })
      // Attempt to start the initial scrape. If it doesn't work, show an error toast but allow the modal to close.
      try {
        await startScrapeNow({
          scrapeTemplateId: createScrapeTemplateResult.createScrapeTemplate.id,
        })
      } catch (error) {
        notification.error({
          message: 'Failed to start scrape',
          description: error instanceof Error ? error.message : 'Please contact GovWell support.',
        })
      }
    }

    await refetch()
    onClose()
  }, [
    form,
    existingScrapeTemplate,
    updateScrapeTemplate,
    createScrapeTemplate,
    refetch,
    onClose,
    announceFirstInvalidField,
    startScrapeNow,
    notification,
    organizationId,
  ])

  return (
    <Modal onClose={onClose}>
      <Modal.Header>{isEditMode ? 'Edit External Website' : 'Add External Website'}</Modal.Header>
      <Modal.Content>
        <VStack gap={4}>
          <T.Body>Enter the URL of the external website you want to scrape. </T.Body>
          <form.AppField
            name="startUrl"
            validators={{
              onBlur: ({ value }) => {
                if (!URL.canParse(value)) {
                  return 'Please enter a valid URL.'
                }
                if (value.endsWith('/')) {
                  return 'Please remove the trailing slash.'
                }
              },
            }}
          >
            {(field) => (
              <field.Input label="URL" placeholder="https://example.com" field={field} isRequired />
            )}
          </form.AppField>
          <ConditionalFormFields form={form} fields={CRAWLER_TYPE_FIELDS} show={isGovWellStaff}>
            <form.AppField name="crawlerType">
              {(field) => (
                <field.Select
                  field={field}
                  label="Crawler Mode"
                  options={CRAWLER_TYPE_OPTIONS}
                  getOptionKey={getCrawlerTypeOptionKey}
                />
              )}
            </form.AppField>
          </ConditionalFormFields>
          <>
            <T.BodySmall>
              The scraper will access the page at this URL and use it as the starting point of a web
              crawl.
            </T.BodySmall>
            <T.BodySmall>
              During the crawl, any links that match this URL (whether http or https, www or
              non-www) will be accessed, and those pages will be added to the crawl recursively.
            </T.BodySmall>
          </>
        </VStack>
      </Modal.Content>
      <Modal.Footer
        confirmText={isEditMode ? 'Save changes' : 'Add external website'}
        onConfirm={handleSubmit}
      />
    </Modal>
  )
}

export default React.memo(ScrapeTemplateModal)
